import React, { useRef, useMemo } from "react"
import {
    FilterChooser,
    FilterChooserProps
} from "../../../../../Common/Button/FilterChooser"
import {
    SortChooser,
    SortChooserProps
} from "../../../../../Common/Button/SortChooser"
import {
    useHotKey,
    Button,
    SimpleDropdown,
    IconNext,
    IconBack
} from "dash-styled"
import { JobVersionItem } from "../../../../../../Models/API/JobModel"
import { OutlinedBackButton } from "../../../../../Common/Button/OutlinedBackButton"

interface JobPanelNavHeaderProps<M> {
    mode: "selected" | "all"
    cursor?: { foundAt: number; totalCount: number }
    sort?: Omit<SortChooserProps<M>, "className">
    filter?: Omit<FilterChooserProps<M>, "className">
    versionsList: JobVersionItem[]
    selectedVersion: number
    onClearSelection?: () => void
    onPrev?: () => void
    onNext?: () => void
    containerClassName?: string
    onClose?: () => void
    disableSortAndFilter?: boolean
    onChangeSelectedVersion: (version: number) => void
}

export function JobPanelNavHeader<M>({
    mode,
    onPrev,
    onNext,
    onClearSelection,
    cursor,
    sort,
    filter,
    versionsList,
    selectedVersion,
    containerClassName,
    onClose,
    disableSortAndFilter,
    onChangeSelectedVersion
}: JobPanelNavHeaderProps<M>) {
    const focusRef = useRef<HTMLDivElement>(null)
    const chooserClassNameClosed =
        "w-max-content transition-none mx-2 bg-transparent text-rhino-250 shadow-none rounded"
    const chooserClassNameOpen =
        "transition-none ml-2 mx-2 bg-rhino-100 text-smoke-610 shadow-none rounded"
    const text = mode === "selected" ? "Selected data" : "Data"

    useHotKey("ArrowRight", onNext)
    useHotKey("ArrowLeft", onPrev)

    const version = useMemo<JobVersionItem | undefined>(
        () => versionsList[selectedVersion],
        [selectedVersion, versionsList]
    )

    const versionDropdownTitle = useMemo(
        () =>
            `Job Version: ${
                selectedVersion > 0 ? version?.versionName : "Latest version"
            } (of ${versionsList.length})`,
        [version, versionsList.length, selectedVersion]
    )

    return (
        <div
            className={
                "px-4 bg-grape-900 text-sm w-full min-h-16 h-16 flex justify-between items-center z-20 " +
                containerClassName
            }
            ref={focusRef}
        >
            <div className="absolute left-0 right-0 justify-center flex items-center text-white pointer-events-none">
                <div className="justify-center flex items-center pointer-events-auto">
                    {(cursor === undefined || cursor.totalCount === 0) && (
                        <div className="">No matching data points</div>
                    )}
                    {cursor !== undefined && cursor.totalCount > 0 && (
                        <>
                            <div className="mr-2">{text}</div>
                            <Button
                                variant="contained"
                                color="plain"
                                className={`${chooserClassNameClosed} ${
                                    onPrev === undefined ? "opacity-25" : ""
                                }`}
                                onClick={onPrev}
                                disabled={onPrev === undefined}
                                testId="prev-button"
                                aria-label="previous data point"
                            >
                                <IconBack />
                            </Button>
                            <div className="mx-2">{cursor.foundAt + 1}</div>
                            <Button
                                variant="contained"
                                color="plain"
                                className={`${chooserClassNameClosed} ${
                                    onNext === undefined ? "opacity-25" : ""
                                }`}
                                onClick={onNext}
                                disabled={onNext === undefined}
                                testId="next-button"
                                aria-label="next data point"
                            >
                                <IconNext />
                            </Button>
                            <div className="ml-2">of {cursor.totalCount}</div>
                        </>
                    )}
                    {mode === "selected" ? (
                        onClearSelection === undefined ? null : (
                            <Button
                                testId="modal-clear-selection"
                                variant="contained2"
                                color="plain"
                                size="xs"
                                className={chooserClassNameClosed}
                                onClick={onClearSelection}
                            >
                                Clear selection
                            </Button>
                        )
                    ) : (
                        <div className="text-grape-900 flex">
                            {sort === undefined ||
                            (cursor !== undefined &&
                                cursor.totalCount === 0) ? null : (
                                <SortChooser
                                    {...sort}
                                    testId="modal-sort-chooser"
                                    classNameClosed={chooserClassNameClosed}
                                    classNameOpen={chooserClassNameOpen}
                                    disabled={disableSortAndFilter}
                                />
                            )}
                            {filter === undefined ? null : (
                                <FilterChooser
                                    {...filter}
                                    testId="modal-filter-chooser"
                                    classNameClosed={chooserClassNameClosed}
                                    classNameOpen={chooserClassNameOpen}
                                    disabled={disableSortAndFilter}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
            <OutlinedBackButton onClick={onClose} />
            {versionsList.length > 1 ? (
                <SimpleDropdown
                    className="p-2 rounded border border-white border-solid"
                    testId="version-dropdown"
                    items={versionsList}
                    title={versionDropdownTitle}
                    anchorPosition="right"
                    color="light"
                    onChange={onChangeSelectedVersion}
                />
            ) : null}
        </div>
    )
}
